/* eslint-disable react/jsx-key */
import React, { useEffect, useRef, useMemo, useState } from 'react';

import './quickListing.css';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  CircularProgress,
  Flex,
  useDisclosure,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';
import Flatpickr from 'react-flatpickr';
import { useHistory } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';

import QuickListingDetails from 'components/modules/QuickListingDetails';
import QuickListingFilters from 'components/modules/QuickListingFilters';
import Button from 'components/primitives/Button';
import PaginationView from 'components/primitives/Pagination';
import { Search, Listing as ListingIcon } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { routesSettings } from 'constants/routes';
import ucFirstMultiple from 'utils/string/ucFirstMultiple';
import { useTheme } from 'utils/theme';

import { QuickListingGeneratedProps } from './QuickListing.props';

const widthPerAccessor = {
  listingType: 150,
  price: 150,
  undefined: undefined,
};

const QuickListingView = ({
  data,
  meta,
  isLoading,
  analytics,
  tableOptions,
  setTableOptions,
  ...props
}: QuickListingGeneratedProps): JSX.Element => {
  const theme = useTheme();
  const history = useHistory();
  const filterModal = useDisclosure();
  const searchRef = useRef() as React.MutableRefObject<HTMLInputElement>;

  const [dateRange, setDateRange] = useState<Date[] | undefined>();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const columns = useMemo(
    () => [
      {
        Header: 'Property Type',
        accessor: 'property_type',
        Cell: ({ value }: any) => ucFirstMultiple(value),
      },
      {
        Header: 'Address',
        accessor: 'formattedAddress',
      },
      {
        Header: 'Estimated Value',
        accessor: 'market_price',
        Cell: ({ value }: any) => formatPriceRange(value),
      },
      {
        Header: 'Bedrooms',
        accessor: 'bedroom',
      },
      {
        Header: 'Garage',
        accessor: 'garage',
        Cell: ({ value }: any) => `${value} Bay`,
      },
      {
        Header: 'Matches',
        accessor: 'matchCount',
        Cell: ({ value }: any) => (value ? value : 0),
      },
      {
        Header: 'Buyer Match Date',
        accessor: 'createdAt',
      },
    ],
    []
  );
  const id = useMemo(() => query.get('id'), [location.search]);

  const currentIndex = useMemo(
    () => (id ? data.findIndex((d) => d._id === id) : -1),
    [data, id]
  );

  const onPrev = () => {
    if (currentIndex > 0) {
      props.setListing(data[currentIndex - 1]);
    }
  };

  const onNext = () => {
    if (currentIndex < data.length - 1) {
      props.setListing(data[currentIndex + 1]);
    }
  };

  const handleDateRangeChange = (selectedDates: Date[]) => {
    if (selectedDates.length !== 2) return;

    setDateRange(selectedDates);

    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;

      // Format the dates as needed for the backend (e.g., 'YYYY-MM-DD')
      const startDate = start.toISOString().split('T')[0];
      const endDate = end.toISOString().split('T')[0];

      history.push({
        pathname: location.pathname,
        search:
          '?' +
          new URLSearchParams({
            ...Object.fromEntries(query.entries()),
            startDate,
            endDate,
          }).toString(),
      });
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      data,
      // @ts-ignore
      columns,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortBy: isLoading,
    },
    useSortBy
  );

  const formatNumber = (num: number): string => {
    if (num >= 1_000_000)
      return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
  };

  const formatPriceRange = (range: string): string => {
    if (!range) return '-';

    const [min, max] = range.split('-').map((val) => Number(val.trim()));

    const format = (num: number) => {
      if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
      if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
      if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
      return `$${num}`;
    };

    return `${format(min)} - ${format(max)}`;
  };

  const onPaginate = (page: number, pageSize: number) => {
    setTableOptions((prevState) => {
      return {
        ...prevState,
        page: prevState.limit !== pageSize ? 1 : page,
        limit: pageSize,
      };
    });
  };

  useEffect(() => {
    setTableOptions((prevState) => {
      if (!isEmpty(sortBy)) {
        const { id, desc } = sortBy[0];
        return {
          ...prevState,
          sort: id,
          direction: desc ? 'desc' : 'asc',
        };
      } else if (isEmpty(sortBy) && !!prevState.sort) {
        const psCopy = { ...prevState };
        delete psCopy.sort;
        delete psCopy.direction;

        return psCopy;
      }

      return prevState;
    });
  }, [sortBy]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="type5" weight="700" color="primary">
          Quick Listing
        </Typography>
      </Box>
      <Box
        mt={5}
        borderRadius="10px"
        p={5}
        bg="#fff"
        boxShadow="0px 2px 10px rgba(149, 149, 149, 0.18)"
      >
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="flex-start"
          columnGap="16px" // spacing between cards in a row
          rowGap="0px" // no extra spacing between rows
        >
          {/* Card 1 */}
          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="160px"
          >
            <Flex align="center" gap={4} mb={2}>
              <Typography variant="type10" weight="600" color="shade10">
                TOTAL SINCE
              </Typography>
              {dateRange && (
                <button
                  style={{ color: 'rgb(28, 0, 102)', fontSize: 9 }}
                  onClick={() => {
                    setDateRange(undefined);
                    history.push(`${routesSettings.QUICK_LISTING.path}`);
                  }}
                >
                  Reset
                </button>
              )}
            </Flex>

            <Flex align="center" gap={4}>
              <Typography variant="type4" weight="600" color="shade5">
                {analytics?.totalSince
                  ? formatNumber(analytics?.totalSince)
                  : 0}
              </Typography>
              <Flatpickr
                placeholder="MM/DD/YYYY filter date"
                options={{
                  mode: 'range',
                  dateFormat: 'm-d-Y',
                  maxDate: 'today',
                  onClose: (selectedDates: Date[]) => {
                    handleDateRangeChange(selectedDates);
                  },
                }}
                value={dateRange}
                onChange={handleDateRangeChange}
                className="custom-flatpickr"
              />
            </Flex>
          </Box>

          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="190px"
          >
            <Typography variant="type10" weight="600" color="shade10">
              THIS MONTH
            </Typography>
            <Flex align="center" gap={2} mt={2}>
              <Typography variant="type4" weight="600" color="shade5">
                {analytics.counts?.thisMonth ? analytics.counts?.thisMonth : 0}
              </Typography>
              <Box
                px={1}
                py={1}
                bg={
                  analytics.percentages?.thisMonth < 0
                    ? 'orange.100'
                    : 'green.100'
                }
                color={
                  analytics.percentages?.thisMonth < 0
                    ? 'orange.700'
                    : 'green.700'
                }
                fontSize="9px"
                borderRadius="8px"
              >
                {analytics.percentages?.thisMonth >= 0 ? '+' : ''}
                {analytics.percentages?.thisMonth ?? 0}% since last month
              </Box>
            </Flex>
          </Box>

          {/* Card 3 */}
          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="170px"
          >
            <Typography variant="type10" weight="600" color="shade10">
              THIS WEEK
            </Typography>
            <Flex align="center" gap={2} mt={2}>
              <Typography variant="type4" weight="600" color="shade5">
                {analytics.counts?.thisWeek ? analytics.counts?.thisWeek : 0}
              </Typography>
              <Box
                px={1}
                py={1}
                bg={
                  analytics.percentages?.thisWeek < 0
                    ? 'orange.100'
                    : 'green.100'
                }
                color={
                  analytics.percentages?.thisWeek < 0
                    ? 'orange.700'
                    : 'green.700'
                }
                fontSize="9px"
                borderRadius="8px"
              >
                {analytics.percentages?.thisWeek >= 0 ? '+' : ''}
                {analytics.percentages?.thisWeek
                  ? analytics.percentages?.thisWeek
                  : 0}
                % from last week
              </Box>
            </Flex>
          </Box>

          {/* Card 4 */}
          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="160px"
          >
            <Typography variant="type10" weight="600" color="shade10">
              24 HOURS
            </Typography>
            <Flex align="center" gap={2} mt={2}>
              <Typography variant="type4" weight="600" color="shade5">
                {analytics.counts?.last24Hours
                  ? analytics.counts?.last24Hours
                  : 0}
              </Typography>
              <Box
                px={1}
                py={1}
                bg={
                  analytics.percentages?.last24Hours < 0
                    ? 'orange.100'
                    : 'green.100'
                }
                color={
                  analytics.percentages?.last24Hours < 0
                    ? 'orange.700'
                    : 'green.700'
                }
                fontSize="9px"
                borderRadius="8px"
              >
                {analytics.percentages?.last24Hours >= 0 ? '+' : ''}
                {analytics.percentages?.last24Hours
                  ? analytics.percentages?.last24Hours
                  : 0}
                % from last day
              </Box>
            </Flex>
          </Box>

          {/* Card 5 */}
          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="190px"
          >
            <Flex align="flex-start" gap={4}>
              <Box display="flex" flexDirection="column" gap="8px">
                <Typography variant="type10" weight="600" color="shade10">
                  TOP DISTRICTS
                </Typography>
                {analytics.topDistricts && analytics.topDistricts.length > 0 ? (
                  analytics.topDistricts.map((district: any, i: number) => (
                    <Flex key={i} gap={2}>
                      <Typography variant="type10" weight="600" color="shade5">
                        {i + 1}.
                      </Typography>
                      <Typography variant="type10" weight="600" color="shade5">
                        {district.name || 'N/A'}
                      </Typography>
                    </Flex>
                  ))
                ) : (
                  <Typography variant="type10" weight="500" color="shade5">
                    No district data available
                  </Typography>
                )}
              </Box>
            </Flex>
          </Box>

          {/* Card 6 */}
          <Box
            bg="white.100"
            border="1px solid"
            borderColor="gray.300"
            borderRadius="5px"
            p={4}
            textAlign="center"
            width="220px"
          >
            <Typography variant="type10" weight="600" color="shade10">
              CONVERSION RATE
            </Typography>
            <Flex align="center" gap={2} mt={2}>
              <Typography variant="type4" weight="600" color="shade5">
                {analytics.conversions
                  ? (
                      (analytics.conversions.converted * 100) /
                      analytics.conversions.total
                    ).toFixed(1)
                  : 0}
                %
              </Typography>
              <Box px={1} py={1} fontSize="9px">
                <Typography variant="type10" color="shade1">
                  {analytics.conversions
                    ? `${analytics.conversions.converted} / ${analytics.conversions.total} `
                    : '0 of 0 '}
                  created a new account since
                </Typography>
              </Box>
            </Flex>
          </Box>
        </Box>
      </Box>

      <Box
        mt={5}
        borderRadius={10}
        p={5}
        minHeight="500px"
        bg="#fff"
        filter="drop-shadow(0px 2px 10px rgba(149, 149, 149, 0.18))"
      >
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          alignItems="Box-start"
        >
          <Box height="30px" alignItems="center" display="flex">
            <ListingIcon color="shade10" width={12} height={12} />
            <Box mx={3}>
              <Typography variant="type8" weight="600" color="shade10">
                QUICK LISTING ACTIVITIES
              </Typography>
            </Box>
            {isLoading ? (
              <CircularProgress
                isIndeterminate
                size="20px"
                color={theme.colors.primary}
              />
            ) : (
              <Typography variant="type10" color="shade5">
                {meta?.count ? meta?.count : 0} Listing
              </Typography>
            )}
          </Box>
          <HStack spacing={5}>
            <InputGroup size="sm" width={300}>
              <InputLeftAddon borderRadius={10} backgroundColor="transparent">
                <Search width="14px" height="14px" />
              </InputLeftAddon>
              <Input
                ref={searchRef}
                onChange={(a: any) => props.onSearchKey(a.target.value)}
                borderRadius={10}
                borderLeft={'none'}
                placeholder={'Search'}
              />
            </InputGroup>
            <Button variant="small" onClick={filterModal.onOpen}>
              Filter
            </Button>
          </HStack>
        </Box>
        <Table mt={7} {...getTableProps()}>
          <Thead backgroundColor="shade12">
            {headerGroups.map((headerGroup: any) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <Th
                    {
                      // @ts-ignore
                      ...column.getHeaderProps(column.getSortByToggleProps())
                    }
                  >
                    <Box>
                      <Typography variant="type9" weight="600" color="shade5">
                        {column.Header}
                      </Typography>
                      <Box ml={2}>
                        {
                          // @ts-ignore
                          column.isSorted ? (
                            <Box>
                              {
                                // @ts-ignore
                                column.isSortedDesc ? (
                                  <TriangleDownIcon aria-label="sorted descending" />
                                ) : (
                                  <TriangleUpIcon aria-label="sorted ascending" />
                                )
                              }
                            </Box>
                          ) : (
                            <Box width="12px" height="5px" />
                          )
                        }
                      </Box>
                    </Box>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody mt={7} {...getTableBodyProps()}>
            {rows.map((row: any, i) => {
              prepareRow(row);
              return (
                <Tr
                  backgroundColor={i % 2 !== 0 ? 'shade18' : 'transparent'}
                  cursor="pointer"
                  onClick={() => props.setListing(row.original)}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell: any) => (
                    <Td
                      {...cell.getCellProps()}
                      // @ts-ignore
                      width={widthPerAccessor[cell.column.id]}
                    >
                      <Typography variant="type8" color="shade5">
                        {cell.render('Cell')}
                      </Typography>
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>

        <Box mt={5} width="100%" display="flex" justifyContent="end">
          <PaginationView
            disabled={isLoading}
            total={meta.count}
            current={meta.page}
            pageSize={tableOptions.limit}
            onChange={onPaginate}
          />
        </Box>
      </Box>
      <QuickListingFilters {...filterModal} />
      <QuickListingDetails
        id={id}
        onClose={() => history.push(`${routesSettings.QUICK_LISTING.path}`)}
        onNext={onNext}
        onPrev={onPrev}
        isOpen={Boolean(id)}
        disablePrev={currentIndex === 0}
        disableNext={currentIndex === data.length - 1}
      />
    </Box>
  );
};

export default QuickListingView;
